import { Button, Col, LoaderOverlay, Row, SwitchNav, Form } from "@emanprague/ppaskit";
import { Check, CollectionCheck, Input } from "@frui.ts/bootstrap";
import { ResponsiveTable } from "@frui.ts/dataviews";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView, View } from "@frui.ts/views";
import EmptyDataMessage from "components/EmptyDataMessage";
import FilterButton from "controls/filterButton";
import SupplyPointInline from "controls/supplyPointInline";
import type { DataColumn } from "controls/tableHelper";
import { createColumnsContext } from "controls/tableHelper";
import DatePicker from "controls/twoway/DatePicker";
import RangeInput from "controls/twoway/RangeInput";
import SupplyPointSelect from "controls/twoway/SupplyPointSelect";
import type AdvanceListItem from "entities/advanceListItem";
import { getIconByStateType } from "helpers/financeHelper";
import { getSupplyPointTypeIcon } from "helpers/supplyPointHelper";
import { observer } from "mobx-react-lite";
import React from "react";
import AdvancesPageViewModel, { defaultAmountRange } from "viewModels/finance/advancesPageViewModel";
import { getColoredLabelFinance } from "helpers/labelHelper";
import ExportButtons from "./exportButtons";

const columns: DataColumn<AdvanceListItem, AdvancesPageViewModel>[] = [
  {
    titleFactory: ({ vm }) => (
      <Check
        id="advanceCheckAll"
        custom
        target={vm}
        property="isAllSelected"
        threeState
        label={<div className="sr-only">{vm.translate("check_all_advance_context")}</div>}
      />
    ),
    valueFormatter: ({ item, context }) => (
      <CollectionCheck
        id={`advanceCheck-${item.id}`}
        custom
        label={
          <div className="sr-only">
            {context.vm.translate("check_advance_context", {
              period: item.period ?? "",
            })}
          </div>
        }
        target={context.vm}
        property="selectedItems"
        value={item}
      />
    ),
    responsiveVisible: false,
  },
  {
    property: "supplyPoints",
    titleFactory: ({ vm, tg }) => (
      <>
        <span id="supplyPointIdsFilterTitle">{tg("general.filter_supply_point")}</span>
        <FilterButton
          id="supplyPointIdsFilter"
          isActive={!!vm.filter.supplyPointIds?.length}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("supplyPointIds")}
          onConfirm={vm.applyFilterAndLoad}>
          <SupplyPointSelect
            target={vm.filter}
            property="supplyPointIds"
            supplyPoints={vm.supplyPoints}
            multiselect
            translateFunc={vm.translateGeneral}
          />
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ tg }) => tg("general.filter_supply_point"),
    valueFormatter: valueProps => {
      const { supplyPoints } = valueProps.item;
      const commodityId = supplyPoints?.[0]?.commodityId ?? -1;

      return (
        <>
          {valueProps.item.supplyPoints && (
            <>
              {valueProps.item.supplyPoints?.length === 1 ? (
                valueProps.item.supplyPoints.map(x => (
                  <SupplyPointInline
                    key={x.id}
                    supplyPoint={x}
                    commodityType={valueProps.context.vm.enumsService.getCommodityType(x.commodityId)}
                    state={valueProps.item.state}
                    commodityAlt={valueProps.context.vm.getCommodityIconAlt(commodityId)}
                  />
                ))
              ) : (
                <>
                  <img
                    className="mr-2"
                    width="22"
                    height="21"
                    draggable={false}
                    src={getSupplyPointTypeIcon(
                      valueProps.context.vm.enumsService.getCommodityType(commodityId),
                      valueProps.item.state === "unpaid" ? "red" : "yellowAndLightBlue"
                    )}
                    alt={valueProps.context.vm.getCommodityIconAlt(commodityId)}
                  />
                  {getColoredLabelFinance(
                    valueProps,
                    valueProps.item.supplyPoints.length.toString() +
                      " " +
                      valueProps.context.tm("supply_point", valueProps.item.supplyPoints.length).toLowerCase()
                  )}
                </>
              )}
            </>
          )}
        </>
      );
    },
  },
  {
    property: "period",
    titleFactory: ({ ta }) => ta("attributes", "period"),
    valueFormatter: valueProps => getColoredLabelFinance(valueProps),
  },
  {
    property: "advanceFrequencyId",
    titleFactory: ({ vm, ta }) => (
      <>
        <span id="advanceFrequencyFilterTitle">{ta("attributes", "frequency")}</span>
        <FilterButton
          id="advanceFrequencyFilter"
          isActive={!!vm.filter.advanceFrequencyId}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("advanceFrequencyId")}
          onConfirm={vm.applyFilterAndLoad}>
          {vm.enumsService.getValues("advanceFrequencies").map((item, index) => (
            <Check
              custom
              key={item.id}
              value={item.id}
              target={vm.filter}
              property="advanceFrequencyId"
              label={item.name}
              className="mt-1"
              type="radio"
            />
          ))}
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("attributes", "frequency"),
    valueFormatter: ({ item, context }) =>
      getColoredLabelFinance({ item }, context.vm.enumsService.getValue("advanceFrequencies", item.advanceFrequencyId)?.name),
  },
  {
    property: "vs",
    titleFactory: ({ vm, ta }) => (
      <>
        <Form.Label id="vsFilterTitle" htmlFor="vs-search-field">
          {ta("advance", "vs")}
        </Form.Label>
        <FilterButton
          id="vsFilter"
          isActive={!!vm.filter.vs}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("vs")}
          onConfirm={vm.applyFilterAndLoad}>
          <Input id="vs-search-field" target={vm.filter} property="vs" placeholder={vm.translateGeneral("search")} />
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("advance", "vs"),
    valueFormatter: valueProps => getColoredLabelFinance(valueProps),
  },
  {
    property: "dueDate",
    titleFactory: ({ vm, ta }) => (
      <>
        <span id="dueDateFilterTitle">{ta("advance", "due_date")}</span>
        <FilterButton
          id="dueDateFilter"
          isActive={!!vm.filter.dueDateFrom || !!vm.filter.dueDateTo}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("dueDateFrom", "dueDateTo")}
          onConfirm={vm.applyFilterAndLoad}>
          <Row className="align-items-center">
            <Col className="mr-n3">
              <Form.Label htmlFor="date-filter-from" className="date-filter" aria-describedby="dueDateFilterTitle">
                {vm.translateGeneral("filter_date_from")}
              </Form.Label>
              <DatePicker
                id="date-filter-from"
                target={vm.filter}
                property="dueDateFrom"
                selectsStart
                startDate={vm.filter.dueDateFrom}
                endDate={vm.filter.dueDateTo}
                maxDate={vm.filter.dueDateTo}
              />
            </Col>
            <Col>
              <Form.Label htmlFor="date-filter-to" className="date-filter to" aria-describedby="dueDateFilterTitle">
                {vm.translateGeneral("filter_date_to")}
              </Form.Label>
              <DatePicker
                id="date-filter-to"
                target={vm.filter}
                property="dueDateTo"
                selectsEnd
                startDate={vm.filter.dueDateFrom}
                endDate={vm.filter.dueDateTo}
                minDate={vm.filter.dueDateFrom}
              />
            </Col>
          </Row>
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("advance", "due_date"),
    valueFormatter: valueProps =>
      getColoredLabelFinance(valueProps, valueProps.context.vm.localization.formatDate(valueProps.value)),
  },
  {
    property: "amount",
    titleFactory: ({ vm, ta }) => (
      <>
        <span id="amountFilterTitle">{ta("advance", "amount")}</span>
        <FilterButton
          id="amountFilter"
          isActive={!!vm.filter.minAmount || !!vm.filter.maxAmount}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("minAmount", "maxAmount")}
          onConfirm={vm.applyFilterAndLoad}>
          <RangeInput
            target={vm}
            property="amountRange"
            allowUserOverride={true}
            min={defaultAmountRange[0]}
            max={defaultAmountRange[1]}
            step={100}
            disabled={false}
            autofocus
            suffix={" " + vm.translateGeneral("currency")}
            name="rangeslider"
            form="rangeform"
            controlAriaDescribedBy="amountFilterTitle"
          />
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("advance", "amount"),
    valueFormatter: valueProps => (
      <span className="text-nowrap">
        {getColoredLabelFinance(
          valueProps,
          valueProps.context.vm.localization.formatNumber(valueProps.value) +
            " " +
            valueProps.context.vm.translateGeneral("currency")
        )}
      </span>
    ),
    cellClassName: "cell-right",
    headerClassName: "cell-right",
  },
  {
    property: "stateText",
    titleFactory: ({ vm, ta }) => (
      <>
        <span id="stateFilterTitle">{ta("advance", "state")}</span>
        <FilterButton
          id="stateFilter"
          isActive={!!vm.filter.state}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={vm.initializeStateFilter}
          onConfirm={vm.applyStateFilter}>
          {vm.stateTypes.map((item, index) => (
            <Check
              custom
              key={item.id}
              value={item.id}
              target={vm}
              property="selectedState"
              label={item.name}
              className="mt-1"
              type="radio"
            />
          ))}
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("advance", "state"),
    valueFormatter: valueProps => (
      <span className="text-nowrap">
        <img
          className="mr-2"
          width="22"
          height="22"
          draggable={false}
          src={getIconByStateType(valueProps.item.state)}
          role="presentation"
        />
        {getColoredLabelFinance(valueProps)}
      </span>
    ),
  },
  {
    titleFactory: ({ vm }) => <span className="sr-only">{vm.translateGeneral("action_button")}</span>,
    valueFormatter: ({ item, context }) => (
      <>
        {item.allowToPay ? (
          <Button
            onClick={() => context.vm.openDetail(item)}
            size="sm"
            className="btn-responsive-block"
            aria-label={context.vm.getActionButtonAriaContext(item, "pay_button")}>
            {context.vm.translateGeneral("pay_button")}
          </Button>
        ) : (
          <Button
            onClick={() => context.vm.openDetail(item)}
            size="sm"
            variant="secondary"
            className="btn-responsive-block"
            aria-label={context.vm.getActionButtonAriaContext(item, "button_more")}>
            {context.vm.translateGeneral("button_more")}
          </Button>
        )}
      </>
    ),
    responsiveTitle: false,
    cellClassName: "cell-right",
  },
];

const advancesPageListView: ViewComponent<AdvancesPageViewModel> = observer(({ vm }) => {
  const { translateGeneral: tg } = vm.localization;

  const navLinks = [
    {
      id: "all",
      title: `${tg("general.filter_all")}`,
      href: "#",
      onClick: preventDefault(() => vm.clearFilter("state")),
    },
    {
      id: "paid",
      title: `${vm.translate("filter_paid")}`,
      href: "#",
      onClick: preventDefault(() => vm.applySwitchStateFilter("paid", "payment_in_process")),
    },
    {
      id: "unpaid",
      title: `${vm.translate("filter_unpaid")}`,
      href: "#",
      onClick: preventDefault(() => vm.applySwitchStateFilter("unpaid", "before_payment")),
    },
  ];

  return (
    <LoaderOverlay loading={vm.busyWatcher.isBusy}>
      <Row className="mb-3 justify-content-between align-items-center">
        <Col xs={12} md="auto" className="py-2" style={{ display: "flex", alignItems: "end", gap: "1rem" }}>
          <SwitchNav
            links={navLinks}
            size="sm"
            activeId={vm.switchStateActiveId}
            ariaDescribedBy={vm.navigationName}
            legend={vm.name}
          />
          <Check
            custom
            label={vm.translate("display_without_advance_regulation")}
            target={vm}
            property="displayPastAdvancesOnly"
            onValueChanged={vm.displayPastAdvancesOnlyChanged}
          />
        </Col>
        <Col xs={12} md="auto" className="py-2" style={{ display: "flex", gap: "0.5rem" }}>
          {vm.canDownload && <ExportButtons vm={vm} />}
        </Col>
        <Col xs={12} md={4} className="py-2 order-1 order-md-3">
          <Form.Label srOnly htmlFor="search-field">
            {vm.translate("search_placeholder")}
          </Form.Label>
          <Input
            id="search-field"
            size="sm"
            target={vm.filter}
            property="search"
            placeholder={vm.translate("search_placeholder")}
            onValueChanged={vm.applyFilterAndLoadDebounced}
          />
        </Col>
      </Row>
      <div className="responsive-table-wrapper">
        <ResponsiveTable
          className="table no-wrap"
          items={vm.items}
          itemKey="id"
          columns={columns}
          context={createColumnsContext(vm)}
        />
      </div>
      {!vm.items?.length && <EmptyDataMessage translateGeneral={tg} />}

      {vm.canLoadData && (
        <div className="load-more-button">
          <Button onClick={vm.loadData} variant="secondary">
            {tg("general.button_load_more")}
          </Button>
        </div>
      )}

      <View vm={vm.activeChild} />
    </LoaderOverlay>
  );
});

registerView(advancesPageListView, AdvancesPageViewModel);
